import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, WritableSignal, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestService } from '@foundation/network/services';
import { NotificationService } from '@foundation/notification';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateDirective, TranslationService } from '@foundation/translations/services';
import { UsersRepository } from '@foundation/users/state';
import { map, tap } from 'rxjs';
import { ADMIN_NAVIGATION_ENTRIES, extractAdditionalAdminNavigationEntries } from '../admin-navigation';

interface AdminDashboardCard {
	label: () => string;
	description: () => string;
	path: string;
}

interface AdminDashboardStat {
	label: () => string;
	path: string;
	count: WritableSignal<number | null>;
	isAlert: boolean;
}

@Component({
	selector: 'lib-admin-dashboard',
	standalone: true,
	imports: [CommonModule, RouterLink, TranslateDirective],
	templateUrl: './admin-dashboard.component.html',
	styleUrl: './admin-dashboard.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminDashboardComponent {
	usersRepository = inject(UsersRepository);
	private _requestService = inject(RequestService);
	private _notificationService = inject(NotificationService);
	private _translationService = inject(TranslationService);
	private _activatedRoute = inject(ActivatedRoute);

	isEmailVerificationLoading = signal(false);

	navigationCards: AdminDashboardCard[] = [
		...ADMIN_NAVIGATION_ENTRIES,
		...extractAdditionalAdminNavigationEntries(this._activatedRoute.parent?.routeConfig),
	].map((navigationEntry) => ({
		label: this._translationService.prep(navigationEntry.label),
		description: this._translationService.prep(navigationEntry.description),
		path: navigationEntry.path,
	}));

	statistics: AdminDashboardStat[] = [
		{
			label: this._translationService.prep('Users'),
			path: '/admin/users',
			count: this._loadTotalCount('/api/users'),
			isAlert: false,
		},
		{
			label: this._translationService.prep('Tasks'),
			path: '/admin/tasks',
			count: this._loadTotalCount('/api/tasks'),
			isAlert: false,
		},
		{
			label: this._translationService.prep('Failed tasks'),
			path: '/admin/tasks',
			count: this._loadTotalCount('/api/tasks', ['failed:~true:exact']),
			isAlert: true,
		},
		{
			label: this._translationService.prep('Emails'),
			path: '/admin/emails',
			count: this._loadTotalCount('/api/mails'),
			isAlert: false,
		},
	];

	private _loadTotalCount(endpoint: string, filters: string[] = []): WritableSignal<number | null> {
		const totalCount = signal<number | null>(null);
		this._requestService
			.getObjectList$(endpoint, {
				page: 1,
				page_size: 1,
				filters,
				ignore_anonymous: true,
				bypass_acls: true,
			})
			.pipe(
				map((response) => (response.error ? null : response.result.totalCount)),
				tap((count) => totalCount.set(count)),
				takeUntilDestroyed()
			)
			.subscribe();
		return totalCount;
	}

	sendEmailVerification() {
		if (this.isEmailVerificationLoading()) return;
		this.isEmailVerificationLoading.set(true);
		this._requestService
			.post$<{ message: string; email: string }>('/api/users/email/send-verification', {})
			.pipe(
				tap((response) => {
					this.isEmailVerificationLoading.set(false);
					if (response.error) {
						this._notificationService.error(response.error.title, response.error.description || 'Failed to send verification email');
						return;
					}
					if (response.result) {
						this._notificationService.success(`A verification email has been sent to ${response.result.email}. Please check your inbox and click the verification link.`, 'Verification email sent!');
					}
				})
			)
			.subscribe();
	}
}
